import { useState, useEffect, useLayoutEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

// Disable the browser's automatic scroll restoration. Without this, hard-refreshing
// a long page (or hitting back) makes the browser jump to the previous scroll
// position BEFORE React has finished hydrating — which momentarily reveals the
// footer of the next page before our ScrollToTop runs.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// Lazy load pages for better initial load performance
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const CaptchaPage = lazy(() => import("@/pages/CaptchaPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const DailyRacesPage = lazy(() => import("@/pages/DailyRacesPage"));
const AcademyPage = lazy(() => import("@/pages/AcademyPage"));
const EsportsTeamPage = lazy(() => import("@/pages/EsportsTeamPage"));
const CoachLoginPage = lazy(() => import("@/pages/CoachLoginPage"));
const CoachDashboardPage = lazy(() => import("@/pages/CoachDashboardPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const ChampionshipPage = lazy(() => import("@/pages/ChampionshipPage"));
const SAGTCupPage = lazy(() => import("@/pages/SAGTCupPage"));
const ChampionsPage = lazy(() => import("@/pages/ChampionsPage"));
const RegistrationPage = lazy(() => import("@/pages/RegistrationPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const SlotMachinePage = lazy(() => import("@/pages/SlotMachinePage"));
const SlotOverlayPage = lazy(() => import("@/pages/SlotOverlayPage"));
const RouletteOverlayPage = lazy(() => import("@/pages/RouletteOverlayPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));

// SAGT Cup Member Pages
const MemberLoginPage = lazy(() => import("@/pages/MemberLoginPage"));
const MemberDashboardPage = lazy(() => import("@/pages/MemberDashboardPage"));
const MemberResetPasswordPage = lazy(() => import("@/pages/MemberResetPasswordPage"));
const SAGTWorldDashboardPage = lazy(() => import("@/pages/SAGTWorldDashboardPage"));
const MemberGate = lazy(() => import("@/components/MemberGate"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));

// Public Members Pages
const MembersListPage = lazy(() => import("@/pages/MembersListPage"));
const DriverProfilePage = lazy(() => import("@/pages/DriverProfilePage"));
const RankingsPage = lazy(() => import("@/pages/RankingsPage"));

// Private League Pages
const PrivateLeagueLoginPage = lazy(() => import("@/pages/PrivateLeagueLoginPage"));
const PrivateLeaguePage = lazy(() => import("@/pages/PrivateLeaguePage"));

// Overlay agent connect handler (member's local Tenths agent redirects here
// to exchange their site session for an overlay key — see CONNECT-HANDLER-BRIEF.md)
const ConnectAgentPage = lazy(() => import("@/pages/ConnectAgentPage"));

// Scroll to top on route change. Uses useLayoutEffect so the scroll happens
// SYNCHRONOUSLY before the browser paints — eliminates the "footer flash"
// that occurred when useEffect deferred the scroll until after paint.
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Belt-and-braces: also reset after the lazy-loaded page resolves, in case
  // the freshly-rendered content shifts the scroll height.
  useEffect(() => {
    const timer = setTimeout(() => window.scrollTo(0, 0), 0);
    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}

// Loading spinner for lazy-loaded pages
function PageLoader() {
  return (
    <div className="min-h-screen carbon-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-[#FFB81C] border-t-transparent rounded-full animate-spin" />
        <span className="font-heading text-xs uppercase tracking-widest text-white/40">Loading...</span>
      </div>
    </div>
  );
}

function VerifiedRoute({ children }) {
  const [isChecking, setIsChecking] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check both sessionStorage and localStorage for verification
    // localStorage persists across sessions (with 24h expiry)
    const checkVerification = () => {
      const sessionVerified = sessionStorage.getItem("sagt_verified") === "1";
      
      // Check localStorage with expiry
      const storedData = localStorage.getItem("sagt_verified_data");
      let localVerified = false;
      if (storedData) {
        try {
          const { verified, expiry } = JSON.parse(storedData);
          if (verified && expiry && Date.now() < expiry) {
            localVerified = true;
            // Sync to sessionStorage for faster subsequent checks
            sessionStorage.setItem("sagt_verified", "1");
          } else {
            // Expired, clean up
            localStorage.removeItem("sagt_verified_data");
          }
        } catch (e) {
          localStorage.removeItem("sagt_verified_data");
        }
      }
      
      setIsVerified(sessionVerified || localVerified);
      setIsChecking(false);
    };
    
    // Small delay to ensure storage is hydrated
    const timer = setTimeout(checkVerification, 10);
    return () => clearTimeout(timer);
  }, []);

  // Show nothing while checking to prevent flash
  if (isChecking) {
    return null;
  }
  
  if (!isVerified) {
    // Preserve the original destination through the captcha so that a shared
    // deep-link like /leaderboard?tab=sagt lands on the leaderboard after
    // verification, not on the generic homepage. `to` is a same-origin path
    // (react-router pathname + search + hash), which CaptchaPage validates
    // before honouring — an open-redirect on the captcha would be a much
    // worse bug than the one we're fixing.
    const dest = `${location.pathname}${location.search}${location.hash}`;
    // Don't send the user through the captcha back to the captcha (or to the
    // landing page they were just on) — that loop wastes a click.
    const skip = dest === "/" || dest.startsWith("/verify");
    const to = skip ? "/verify" : `/verify?to=${encodeURIComponent(dest)}`;
    return <Navigate to={to} replace />;
  }
  
  return children;
}

// Protected route for SAGT Cup Members
function MemberProtectedRoute({ children }) {
  const token = localStorage.getItem("sagt_member_token");
  console.log("MemberProtectedRoute check, token:", token ? "present" : "missing");
  
  if (!token) {
    console.log("No token, redirecting to login");
    return <Navigate to="/member/login" replace />;
  }
  
  return children;
}

function AppContent() {
  const location = useLocation();

  // OBS Streamer Overlay — a fully standalone, chrome-free, transparent route
  // (no navbar/footer, no site background, no captcha) meant for OBS Browser
  // Sources. Short-circuits before the normal app shell renders.
  if (location.pathname.startsWith("/overlay/")) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/overlay/slot-machine/:token" element={<SlotOverlayPage />} />
          <Route path="/overlay/race-roulette/:token" element={<RouletteOverlayPage />} />
        </Routes>
      </Suspense>
    );
  }

  // The public Leagues entry page (linked from the menu) keeps the site nav so users
  // can navigate back. Only the inner authenticated CEO panel runs standalone (like the admin/
  // member dashboards). URL was renamed `/private-league` → `/leagues`; legacy paths still
  // resolve so existing bookmarks & Discord links keep working.
  const isPrivateLeague = location.pathname.startsWith("/leagues") || location.pathname.startsWith("/private-league");
  const isPrivateLeagueLogin = location.pathname === "/leagues/login" || location.pathname === "/private-league/login";
  const isPrivateLeagueApp = isPrivateLeague && !isPrivateLeagueLogin;
  const hideChrome = location.pathname === "/" || location.pathname === "/verify" || location.pathname === "/connect" || isPrivateLeagueApp;
  const isAdmin = location.pathname.startsWith("/admin");
  const isMemberPage = location.pathname.startsWith("/member");
  const isWorldPage = location.pathname.startsWith("/world");

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {!hideChrome && <Navbar />}
      <main className={`flex-1 ${!hideChrome ? "pt-16" : ""}`}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/verify" element={<CaptchaPage />} />
            <Route path="/home" element={<VerifiedRoute><HomePage /></VerifiedRoute>} />
            <Route path="/about" element={<VerifiedRoute><AboutPage /></VerifiedRoute>} />
            <Route path="/shop" element={<VerifiedRoute><ShopPage /></VerifiedRoute>} />
            <Route path="/races" element={<VerifiedRoute><DailyRacesPage /></VerifiedRoute>} />
            <Route path="/esports-team" element={<VerifiedRoute><EsportsTeamPage /></VerifiedRoute>} />
            <Route path="/academy" element={<VerifiedRoute><AcademyPage /></VerifiedRoute>} />
            <Route path="/coach/login" element={<CoachLoginPage />} />
            <Route path="/coach/dashboard" element={<CoachDashboardPage />} />
            <Route path="/coach" element={<Navigate to="/coach/login" replace />} />
            <Route path="/members" element={<Navigate to="/rankings" replace />} />
            <Route path="/rankings" element={<VerifiedRoute><MemberGate title="SAGT Rating — Members Only"><RankingsPage /></MemberGate></VerifiedRoute>} />
            <Route path="/members/:memberId" element={<VerifiedRoute><DriverProfilePage /></VerifiedRoute>} />
            <Route path="/leaderboard" element={<VerifiedRoute><LeaderboardPage /></VerifiedRoute>} />
            <Route path="/events" element={<VerifiedRoute><MemberGate title="Events — Members Only"><EventsPage /></MemberGate></VerifiedRoute>} />
            <Route path="/championship" element={<VerifiedRoute><ChampionshipPage /></VerifiedRoute>} />
            <Route path="/sagt-cup" element={<VerifiedRoute><MemberGate title="SAGT Cup — Members Only"><SAGTCupPage /></MemberGate></VerifiedRoute>} />
            <Route path="/sagt-cup/champions/:seasonId" element={<VerifiedRoute><ChampionsPage /></VerifiedRoute>} />
            <Route path="/register" element={<VerifiedRoute><RegistrationPage /></VerifiedRoute>} />
            <Route path="/slot-machine" element={<VerifiedRoute><SlotMachinePage /></VerifiedRoute>} />
            <Route path="/privacy" element={<VerifiedRoute><PrivacyPage /></VerifiedRoute>} />
            <Route path="/faq" element={<VerifiedRoute><FaqPage /></VerifiedRoute>} />
            <Route path="/admin" element={<AdminPage />} />
            {/* SAGT Cup Member Routes */}
            <Route path="/member/login" element={<VerifiedRoute><MemberLoginPage /></VerifiedRoute>} />
            <Route path="/member/reset-password" element={<VerifiedRoute><MemberResetPasswordPage /></VerifiedRoute>} />
            <Route path="/member/dashboard" element={<MemberProtectedRoute><MemberDashboardPage /></MemberProtectedRoute>} />
            <Route path="/member" element={<MemberProtectedRoute><MemberDashboardPage /></MemberProtectedRoute>} />
            {/* SAGT World (international) dashboard */}
            <Route path="/world" element={<MemberProtectedRoute><SAGTWorldDashboardPage /></MemberProtectedRoute>} />
            <Route path="/world/dashboard" element={<MemberProtectedRoute><SAGTWorldDashboardPage /></MemberProtectedRoute>} />
            {/* Leagues Routes (renamed from `/private-league` — old paths still resolve for existing bookmarks). */}
            <Route path="/leagues/login" element={<VerifiedRoute><PrivateLeagueLoginPage /></VerifiedRoute>} />
            <Route path="/leagues" element={<VerifiedRoute><PrivateLeaguePage /></VerifiedRoute>} />
            <Route path="/private-league/login" element={<Navigate to="/leagues/login" replace />} />
            <Route path="/private-league" element={<Navigate to="/leagues" replace />} />
            {/* Overlay agent connect handler — no captcha gate, no chrome. A
                member arriving here has been sent by software they installed. */}
            <Route path="/connect" element={<ConnectAgentPage />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && !isAdmin && !isMemberPage && !isWorldPage && !isPrivateLeagueApp && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AppContent />
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
